import { useState } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useNorthbeamQuery } from "@hooks/http";
import { type GetSteps } from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";

import { GET_STEPS } from "..";

export interface StepCompletionConfirmationProps {
  // Suffix that will be added after the label of "Mark this step ‘complete’ to let us know you’ve finished "
  readonly labelSuffix: string;
  readonly step: string;
  nextStep?: () => void;
  nextStepDisabled?: boolean;
  nextStepLabel?: string;
  nextStepTooltip?: React.ReactNode;
  toggleStepComplete: () => void;
  onSelect?: () => boolean;
  disabled?: boolean;
}

export const StepCompletionConfirmation = ({
  labelSuffix,
  step,
  nextStep,
  nextStepDisabled,
  nextStepLabel = "Go to next step",
  nextStepTooltip,
  toggleStepComplete,
  onSelect,
  disabled,
}: StepCompletionConfirmationProps) => {
  const { data: onboardingV2Steps } = useNorthbeamQuery<GetSteps>(
    GET_STEPS,
    {},
  );

  const persistedStep = onboardingV2Steps?.me.onboardingSteps?.find(
    (apiStep) => apiStep.step === step.replaceAll("-", "_").toUpperCase(),
  );

  const [isStepComplete, setIsStepComplete] = useState(
    !!persistedStep?.end_time,
  );

  return (
    <div
      className={clsx(
        "p-9 rounded-sm border-t justify-between items-center gap-4 flex flex-row",
        {
          "bg-alien-2": isStepComplete,
          "bg-cosmic text-white": !isStepComplete,
          "bg-gray-2": disabled && !isStepComplete,
        },
      )}
    >
      <div className="h-6 justify-between items-center inline-flex w-full">
        <div className="justify-center items-center gap-2 inline-flex">
          <input
            id="completedStep"
            type="checkbox"
            className={clsx("pb-0 mb-0 mr-2", {
              "checked:before:border-alien+0 checked:before:bg-alien+0":
                isStepComplete,
            })}
            checked={isStepComplete}
            onChange={async (inputValue) => {
              const isChecked = inputValue.target.checked;
              if (!isStepComplete && isChecked && !(onSelect?.() ?? true)) {
                return;
              }

              setIsStepComplete(isChecked);
              toggleStepComplete?.();
            }}
            disabled={disabled}
          />
          <label
            htmlFor="completedStep"
            className="mb-0 pb-0 text-xl font-semibold leading-normal"
          >
            Mark this step ‘Complete’ to let us know you’ve finished{" "}
            {labelSuffix}
          </label>
        </div>
        {isStepComplete && nextStep && (
          <PrimaryButton
            onClick={nextStep}
            className="uppercase"
            disabled={nextStepDisabled}
          >
            <GenericTooltip
              noInfoCircle
              showTooltip={!!nextStepTooltip}
              content={nextStepTooltip}
            >
              <div className="justify-center items-center gap-2 inline-flex">
                <span>{nextStepLabel}</span>
                <i className="fa-sharp fa-regular fa-arrow-right" />
              </div>
            </GenericTooltip>
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};
